import React, {useEffect, useState} from 'react'
import {Container} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import { resetConfirmationDialog, toggleConfirmationDialog, toggleLoader } from '../../shared/actions/setting';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { deleteRole, exportRolesYml, getRoles, protectRole } from '../../utils/mainServices';
import { getLevel } from '../../utils/Utils';
import { isSuper } from '../../utils/Authentication';
import AddNewRole from './AddNewRole';
import FilterHandler from '../../shared/utils/CombinedFilterHandler';
import SearchInput from '../../shared/components/SearchInput';
import useColumnSelector from '../../shared/components/useColumnSelector';

const RolePageTable = () => {
  const dispatch = useDispatch();
  const [allRoleList, setAllRoleList] = useState([]);
  const [filteredRoleList, setFilteredRoleList] = useState([]);
  const {t} = useTranslation();
  const [ deleteRoleId, setDeleteRoleId] = useState(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [protectRoleData, setProtectRoleData] = useState(null)
  const confirmationDialog = useSelector(state => state.setting.confirmationDialog);

  const [isAddRoleVisible, setIsAddRoleVisible] = useState(false);
  const [formType, setFormType] = useState('new');
  const [selectedRole, setSelectedRole] = useState(null);

  const [searchText, setSearchText] = useState('');
  const { ColumnSelector, columnFilter, columnFilterReadOnly } = useColumnSelector('roles-table');

  const fieldsToFilter = ["id", "name", "level"];

  function updateFilteredRoles() {
    setFilteredRoleList(filteredList);
  }

  const {
    handleSearch: handleFilterSearch,
    handleFilter,
    filteredList,
    filters,
  } = FilterHandler(
    updateFilteredRoles,
    allRoleList.map(r => ({ ...r, level: getLevel(r?.id) })),
    fieldsToFilter
  );

  const levelOptions = ["Super Level", "Account Level", "Corporate Level", "Level I", "Level II"];

  useEffect(() => {
    const fetchRoles = async () => {
      dispatch(toggleLoader(true));
      try {
        const data = await getRoles();
        const sorted = data.content.sort((role1, role2) => role1.id - role2.id);
        setAllRoleList(sorted);
        setFilteredRoleList(sorted);

        setSearchText('');
      } catch (error) {
        console.error('Error fetching Roles:', error); 
        toast.error("Roles can't be fetched");
      } finally {
        dispatch(toggleLoader(false));
      }
    };

    fetchRoles();
  }, [dispatch, shouldFetchData]); 

  
  //protect the role
  function handleProtectUser(role) {
    if(!role.protect || isSuper()){
      dispatch(toggleConfirmationDialog({
        isVisible: true,
         type: 'request',
         confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_PROTECT_THIS_ROLE?"),
         confirmationDescription: t("confirmation_msg.THE_OK_ACTION_WILL_SEND_PROTECT_THIS_ROLE"),
      }));
     setProtectRoleData(role);
   } else {
      toast.error("You Can't Unlock the Data.")
   }
  }

  //delete role
  function handleDeleteRole(role) {
    setDeleteRoleId(role.id);
    dispatch(toggleConfirmationDialog({
      isVisible: true,
      confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ROLE_WITH_NAME", { name: role.name }),
      confirmationDescription: t("confirmation_msg.THE_DELETE_ACTION_WILL_REMOVE_THE_ROLE_WITH_NAME", { name: role.name })
    }));
  }
  
  useEffect(() => {
    if (!confirmationDialog || !confirmationDialog.onSuccess) {
      return;
    }

    const handleConfirmation = async () => {
      if(protectRoleData) {
        //Handle Protect Role
        try {
          dispatch(toggleLoader(true));
          await protectRole(
            protectRoleData.id,
            !(protectRoleData.protect),
          );      
          toast.success("Role has been protected");
          setShouldFetchData(prev => !prev);
        } catch (error) {
          if (error.response?.status === 422) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Oops...Role has not been protected");
          }
        } finally {
          setProtectRoleData(null);
          dispatch(toggleLoader(false));
          dispatch(resetConfirmationDialog());
        }
      } else if (deleteRoleId) {
        //delete role
        try {
          dispatch(toggleLoader(true));
          await deleteRole(deleteRoleId);
          toast.success("Role deleted succesfully");
          setShouldFetchData(prev => !prev);
        } catch (error) {
          if (error.response && error.response.status === 422) {
            toast.warn(error.response.data.message);
          } else {
            toast.error("Something went wrong");
          }
        } finally {
          setDeleteRoleId("");
          dispatch(toggleLoader(false));
          dispatch(resetConfirmationDialog());
        }
      }
    }

    handleConfirmation();
  }, [confirmationDialog, deleteRoleId, dispatch, protectRoleData]);

  const handleAddRole = () => {
    setFormType('new');
    setSelectedRole(null);
    setIsAddRoleVisible(true);
  };
  const handleEditRole = (role) => {
    setFormType('edit');
    setSelectedRole(role);
    setIsAddRoleVisible(true);
  };

  const handleViewRole = (role) => {
    setFormType('view');
    setSelectedRole(role);
    setIsAddRoleVisible(true);
  };

  const handleExportRolesYml = async () => {
    dispatch(toggleLoader(true));
    try {
      const response = await exportRolesYml();
      const contentDisposition = response.headers['content-disposition'];
      const fileNameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch ? fileNameMatch[1] : 'roles.zip';
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Roles yml exported successfully');
    } catch (error) {
      toast.error("Roles yml can't be exported");
    } finally {
      dispatch(toggleLoader(false));
    }
  };

  const closeAddRole = (shouldRefresh = false) => {
    setIsAddRoleVisible(false);
    setSelectedRole(null);
    if (shouldRefresh) {
      setShouldFetchData(prev => !prev);
    }
  };

  return (
    <div className={"half-list-container"}>
      <Container>
        <div className={"single-card m-rl-m-8 p-a-16"}>
          <div className={"sa-table-flex sa-table-top"}>
            <span className="sa-table-top-left-items">
              <ColumnSelector/>
              <SearchInput
                value={searchText}
                onValueChange={setSearchText}
                onSearch={handleFilterSearch}
                placeholder="Search Roles"
              />
              <select
                className="sa-filter sa-table-float-left participant-filter"
                onChange={handleFilter}
                name="level"
                value={(filters && filters.level) || ""}
              >
                <option value={""}>{t("title.LEVEL")}</option>
                {levelOptions.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </span>
            <div className="sa-table-top-right-items">
              <span>
                <div className="xin-pull-right">
                  <span className="sa-table-icon-flex sa-table-filter-position">
                    <button
                        className="sa-table-btn-secondary sa-table-btn-secondary-wide sa-table-float-right"
                        onClick={handleExportRolesYml}
                    >
                      <FeatherIcon
                        icon="download"
                        className={"sa-table-icon-size"}/> Export Yml
                    </button>
                    <button
                        className="sa-table-btn-secondary sa-table-float-right sa-table-btn-add-role"
                        onClick={handleAddRole}
                    >
                      <FeatherIcon
                        icon={`plus`}
                        className={"sa-table-icon-size"}/> Add New Role
                    </button>
                  </span>
                </div>
              </span>
            </div>
          </div>
          <div  id="main3" className="sa-table-container xindicate-calc-height">
            <table className="table table-borderless sa-table-width">
              <thead>
              <tr>
                <th hidden={columnFilterReadOnly("Id")} className={"sa-table-head-sticky"}>Id</th>
                <th hidden={columnFilter("Level")} className={"sa-table-head-sticky"}>Level</th>
                <th hidden={columnFilter("Role Name")} className={"sa-table-head-sticky"}>Role Name</th>
                <th hidden={columnFilter("Description")} className={"sa-table-head-sticky"}>Discription</th>
                <th hidden={columnFilter("Actions")} className={"sa-table-head-sticky"}>Actions</th>
              </tr>
              </thead>
              <tbody>
              {filteredRoleList.map((role, index) => (
                  <tr key={index}>
                    <td hidden={columnFilterReadOnly("Id")} className={'sa-table-data'}>{role.id}</td>
                    <td hidden={columnFilter("Level")} className={'sa-table-data'}>{getLevel(role.id)}</td>
                    <td hidden={columnFilter("Role Name")} className={'sa-table-data'}>{role.name}</td>
                    <td hidden={columnFilter("Description")} className={'sa-table-data'}>{role.description}</td>
                    <td hidden={columnFilter("Actions")} className={'sa-table-item-align'}>
                      <div className='sa-table-item-flex'>
                        <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.VIEW")}>
                           <FeatherIcon
                                onClick={() => handleViewRole(role)}
                              icon="eye"
                                className={`table-action` }/>
                      </div>
                        <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.EDIT")}>
                          <FeatherIcon
                                onClick={() => handleEditRole(role)}
                            className={`table-action ` }
                            icon={"edit"}
                          />
                        </div>
                        <div className="sa-table-actions sa-action-m" data-toggle="tooltip" title={t("button.DELETE")}>
                          <FeatherIcon
                            className={`feather-trash + ${role.protect ? 'disable-btn text-lightred' : 'text-red'}`}
                            icon={`trash-2`}
                            onClick={() => {
                              if (!(role.protect)) {
                              handleDeleteRole(role);
                            }
                            }}
                          />
                        </div>
                        <div className="sa-table-actions sa-action-m" data-toggle="tooltip"
                           title={role.protect? t("button.UNLOCK") : t("button.LOCK")}>
                           <FeatherIcon
                              onClick={() => {
                                handleProtectUser(role);

                              }}
                              icon={(role.protect) ? "lock" : "unlock"}
                              className={`table-action `}
                           />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRoleList && filteredRoleList.length === 0 && (
              <div className={"empty-results mt-5"}>
                <FeatherIcon icon="info"/>
                <div className={"empty-results-text"}>{t("emptyMsg.THERE_ARE_NO_ROLES")}</div>
              </div>
            )}
          </div>
        </div>

          {isAddRoleVisible && (
            <AddNewRole
                isVisible={isAddRoleVisible}
                onClose={closeAddRole}
                formType={formType}
                selectedRole={selectedRole}
                allRoles={allRoleList}
            />
          )}
      </Container>
    </div>
  )
}

export default RolePageTable;
